import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button } from '@mui/material';
import { isMobile, isTablet } from 'react-device-detect';

const BlockEmbed = Quill.import('blots/block/embed');

class TableBlockEmbed extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute('contenteditable', 'false');
    node.innerHTML = value;
    return node;
  }

  static value(node) {
    return node.innerHTML;
  }
}
TableBlockEmbed.blotName = 'tableEmbed';
TableBlockEmbed.tagName = 'DIV';
TableBlockEmbed.className = 'ql-table-embed';

Quill.register(TableBlockEmbed, true);

function MeetingMinutesEditor(props) {
  const { markdownToQuill, summaryContext, setSummaryContext } = props;
  const [hasChanges, setHasChanges] = useState(false);
  const [initialValue, setInitialValue] = useState(
    markdownToQuill(summaryContext)
  );
  const [quillValue, setQuillValue] = useState(
    markdownToQuill(summaryContext)
  );

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const previousSummaryContextRef = useRef(summaryContext);
  const isInternalUpdateRef = useRef(false);
  const quillRef = useRef(null);
  const initialValueRef = useRef(initialValue);

  const updateHistoryAvailability = useCallback(() => {
    const editor = quillRef.current?.getEditor?.();
    const history = editor?.history;
    if (history) {
      setCanUndo(Boolean(history.stack?.undo?.length));
      setCanRedo(Boolean(history.stack?.redo?.length));
    } else {
      setCanUndo(false);
      setCanRedo(false);
    }
  }, []);

  const handleEditorChange = (value, delta, source) => {
    if (source !== 'user') {
      // Programmatic change (initial load, external update, Quill normalization).
      // Only sync the baseline for comparison — do NOT overwrite quillValue, or we
      // can blank the editor when Quill fires with empty/intermediate content.
      setInitialValue(value);
      initialValueRef.current = value;
      return;
    }
    // User-initiated edit
    isInternalUpdateRef.current = true;
    const hasChangesValue = value !== initialValueRef.current;
    setHasChanges(hasChangesValue);
    setQuillValue(value);
    setSummaryContext(value);
  };

  // Custom undo handler for toolbar
  const handleUndo = useCallback(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill && quill.history) {
        quill.history.undo();
        updateHistoryAvailability();
      }
    }
  }, [updateHistoryAvailability]);

  // Custom redo handler for toolbar
  const handleRedo = useCallback(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      if (quill && quill.history) {
        quill.history.redo();
        updateHistoryAvailability();
      }
    }
  }, [updateHistoryAvailability]);

  // Configuration for the React Quill toolbar with custom undo/redo handlers
  const isMobileView = isMobile || isTablet;
  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: isMobileView
          ? [
              // Mobile view: Hide font family, strikethrough, subscript/superscript, color, link, image
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              ['bold', 'italic', 'underline'],
              ['blockquote'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ indent: '-1' }, { indent: '+1' }],
              ['clean'],
              ['undo', 'redo'],
            ]
          : [
              // Desktop view: Show all options
              [{ font: [] }],
              [{ header: [1, 2, 3, 4, 5, 6, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              ['blockquote'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ script: 'sub' }, { script: 'super' }],
              [{ indent: '-1' }, { indent: '+1' }],
              [{ direction: 'rtl' }],
              [{ color: [] }, { background: [] }],
              ['link', 'image'],
              ['clean'],
              ['undo', 'redo'],
            ],
        handlers: {
          undo: handleUndo,
          redo: handleRedo,
        },
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true,
      },
    }),
    [handleUndo, handleRedo, isMobileView]
  );

  // Only update initialValue/quillValue when summaryContext changes from external source (not from user editing)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      previousSummaryContextRef.current = summaryContext;
      isInternalUpdateRef.current = false;
      return;
    }

    if (previousSummaryContextRef.current !== summaryContext) {
      const nextInitial = markdownToQuill(summaryContext);
      setInitialValue(nextInitial);
      initialValueRef.current = nextInitial;
      setQuillValue(nextInitial);
      setHasChanges(false);
      previousSummaryContextRef.current = summaryContext;
      const editor = quillRef.current?.getEditor?.();
      if (editor?.history) {
        editor.history.clear();
        updateHistoryAvailability();
      }
    }
  }, [markdownToQuill, summaryContext, updateHistoryAvailability]);

  useEffect(() => {
    updateHistoryAvailability();
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const handleChange = () => {
      updateHistoryAvailability();
    };

    editor.on('text-change', handleChange);
    editor.on('selection-change', handleChange);

    return () => {
      editor.off('text-change', handleChange);
      editor.off('selection-change', handleChange);
    };
  }, [updateHistoryAvailability]);

  // Add custom icons for undo/redo buttons
  useEffect(() => {
    const addIcons = () => {
      if (quillRef.current) {
        try {
          const quill = quillRef.current.getEditor();
          const toolbar = quill.getModule('toolbar');

          if (toolbar && toolbar.container) {
            const undoButton = toolbar.container.querySelector('.ql-undo');
            const redoButton = toolbar.container.querySelector('.ql-redo');

            const updateButtonState = () => {
              if (undoButton) {
                undoButton.classList.toggle('disabled', !canUndo);
                undoButton.setAttribute('aria-disabled', (!canUndo).toString());
              }
              if (redoButton) {
                redoButton.classList.toggle('disabled', !canRedo);
                redoButton.setAttribute('aria-disabled', (!canRedo).toString());
              }
            };

            if (undoButton) {
              undoButton.setAttribute('title', 'Undo');
              if (!undoButton.querySelector('svg')) {
                undoButton.innerHTML = `
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 7v6h6"></path>
                    <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"></path>
                  </svg>
                `;
              }
            }

            if (redoButton) {
              redoButton.setAttribute('title', 'Redo');
              if (!redoButton.querySelector('svg')) {
                redoButton.innerHTML = `
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 7v6h-6"></path>
                    <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"></path>
                  </svg>
                `;
              }
            }
            updateButtonState();
          }
        } catch (error) {
          // Error adding undo/redo icons
        }
      }
    };

    // Try immediately
    addIcons();

    // Also try after a short delay to ensure toolbar is rendered
    const timeoutId = setTimeout(addIcons, 100);

    return () => clearTimeout(timeoutId);
  }, [summaryContext, canUndo, canRedo]);

  // ================================================================
  // TABLE INTERACTIVITY (Google-Docs style)
  // On hover:
  //   - Hovering a cell in the FIRST COLUMN shows the ROW toolbar on the
  //     LEFT of that row (vertical orientation).
  //   - Hovering a cell in the FIRST ROW shows the COLUMN toolbar on TOP
  //     of that column (horizontal orientation).
  //   - Hovering the top-left cell shows both toolbars simultaneously.
  // Each toolbar offers insert-before / insert-after / delete actions.
  // Moving the pointer away from the table (and its toolbars) hides them.
  // ================================================================
  useEffect(() => {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;

    // ---- Floating toolbar elements (created lazily) ----
    let rowToolbarEl = null;
    let colToolbarEl = null;
    let activeCell = null;
    let activeTable = null;
    let hideTimer = null;

    // Track cells that have our handlers attached. Using a WeakSet tied to
    // the DOM node identity is critical: after an insert/delete op we call
    // syncToContext(), which updates React state and causes ReactQuill to
    // replace the editor DOM with brand-new nodes. A data-attribute guard
    // would get serialized into the HTML and falsely tell us those new
    // nodes are already wired up, breaking hover/clipboard on subsequent
    // interactions. The WeakSet only contains the original node objects.
    const attachedCells = new WeakSet();

    const cancelHide = () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
    };

    const scheduleHide = () => {
      cancelHide();
      hideTimer = setTimeout(() => {
        hideTimer = null;
        hideToolbars();
      }, 220);
    };

    const BASE_STYLE = [
      'position:fixed',
      'z-index:2147483646',
      'background:#fff',
      'border:1px solid #dadce0',
      'border-radius:8px',
      'box-shadow:0 2px 10px rgba(60,64,67,0.18)',
      'padding:4px',
      'display:none',
      'align-items:center',
      'gap:2px',
      'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
      'user-select:none',
      'pointer-events:auto',
    ].join(';');

    const attachToolbarHoverKeepalive = el => {
      el.addEventListener('mouseenter', cancelHide);
      el.addEventListener('mouseleave', scheduleHide);
    };

    const getRowToolbar = () => {
      if (!rowToolbarEl) {
        rowToolbarEl = document.createElement('div');
        rowToolbarEl.setAttribute('data-genie-tbl-toolbar', 'row');
        rowToolbarEl.style.cssText = BASE_STYLE + ';flex-direction:column';
        document.body.appendChild(rowToolbarEl);
        attachToolbarHoverKeepalive(rowToolbarEl);
      }
      return rowToolbarEl;
    };

    const getColToolbar = () => {
      if (!colToolbarEl) {
        colToolbarEl = document.createElement('div');
        colToolbarEl.setAttribute('data-genie-tbl-toolbar', 'col');
        colToolbarEl.style.cssText = BASE_STYLE + ';flex-direction:row';
        document.body.appendChild(colToolbarEl);
        attachToolbarHoverKeepalive(colToolbarEl);
      }
      return colToolbarEl;
    };

    const hideToolbars = () => {
      cancelHide();
      if (rowToolbarEl) {
        rowToolbarEl.style.display = 'none';
        rowToolbarEl.innerHTML = '';
      }
      if (colToolbarEl) {
        colToolbarEl.style.display = 'none';
        colToolbarEl.innerHTML = '';
      }
      activeCell = null;
      activeTable = null;
    };

    const makeIconBtn = (title, isDanger, svgPaths, onClick) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.title = title;
      btn.setAttribute('aria-label', title);
      const stroke = isDanger ? '#b91c1c' : '#1565c0';
      btn.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svgPaths}</svg>`;
      btn.style.cssText = [
        'display:inline-flex',
        'align-items:center',
        'justify-content:center',
        'width:28px',
        'height:28px',
        'padding:0',
        'border-radius:6px',
        'border:none',
        'background:transparent',
        'cursor:pointer',
        'flex-shrink:0',
        'transition:background 0.12s',
      ].join(';');
      btn.onmouseenter = () => {
        btn.style.background = isDanger ? '#fee2e2' : '#e8f0fe';
      };
      btn.onmouseleave = () => {
        btn.style.background = 'transparent';
      };
      // Use mousedown + preventDefault so the caret inside the cell doesn't blur
      // away before we can grab DOM references.
      btn.onmousedown = e => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      };
      return btn;
    };

    // ---- Sync table DOM → summaryContext ----
    const syncToContext = () => {
      const html = quill.root.innerHTML;
      isInternalUpdateRef.current = true;
      setSummaryContext(html);
      setQuillValue(html);
      setHasChanges(true);
    };

    // ---- Attach handlers to a single cell (editable + show toolbars on hover) ----
    const attachCellHandlers = (cell, table) => {
      cell.setAttribute('contenteditable', 'true');
      cell.style.outline = 'none';
      cell.style.cursor = 'text';
      if (!cell.style.minWidth) cell.style.minWidth = '40px';

      if (attachedCells.has(cell)) return;
      attachedCells.add(cell);

      cell.addEventListener('blur', syncToContext);

      cell.addEventListener('keydown', e => {
        e.stopPropagation();
        if (e.key === 'Tab') {
          e.preventDefault();
          const cells = [...table.querySelectorAll('td, th')];
          const idx = cells.indexOf(cell);
          if (e.shiftKey) {
            if (idx > 0) cells[idx - 1].focus();
          } else {
            if (idx < cells.length - 1) cells[idx + 1].focus();
          }
        }
      });

      // Prevent Quill from capturing clicks/focus inside cells.
      cell.addEventListener('mousedown', e => e.stopPropagation());

      // Quill's clipboard module listens on the editor root and, on paste,
      // uses Quill's own selection (which sits OUTSIDE the table embed).
      // That causes pasted content to land above the table. Stop these
      // events from bubbling up so the browser's native contenteditable
      // clipboard behaviour runs inside the cell, and sync the cell's HTML
      // back into state afterwards so edits are persisted.
      cell.addEventListener('copy', e => {
        e.stopPropagation();
      });
      cell.addEventListener('cut', e => {
        e.stopPropagation();
        setTimeout(syncToContext, 0);
      });
      cell.addEventListener('paste', e => {
        e.stopPropagation();
        setTimeout(syncToContext, 0);
      });

      // Hovering a cell activates the row/column toolbars — but only when
      // the cell is in the first row (column toolbar) and/or first column
      // (row toolbar). Non-qualifying cells must NOT keep a previously
      // shown toolbar alive, so we don't cancel the pending hide here —
      // showToolbarsForCell() only cancels when it actually has something
      // to show.
      cell.addEventListener('mouseenter', () => {
        showToolbarsForCell(cell, table);
      });
      cell.addEventListener('mouseleave', () => {
        scheduleHide();
      });
    };

    const makeAllCellsEditable = table => {
      table.querySelectorAll('td, th').forEach(cell => attachCellHandlers(cell, table));
    };

    const createEmptyRow = (colCount, table) => {
      const newRow = document.createElement('tr');
      for (let i = 0; i < colCount; i++) {
        const td = document.createElement('td');
        newRow.appendChild(td);
        attachCellHandlers(td, table);
      }
      return newRow;
    };

    const createEmptyCell = (isHeader, table) => {
      const cell = document.createElement(isHeader ? 'th' : 'td');
      attachCellHandlers(cell, table);
      return cell;
    };

    // Icons (24x24 viewBox)
    const ICON_ROW_ABOVE =
      '<path d="M3 10h18"/><path d="M3 14h18"/><path d="M3 18h18"/><path d="M12 3v4"/><path d="M10 5l2-2 2 2"/>';
    const ICON_ROW_BELOW =
      '<path d="M3 6h18"/><path d="M3 10h18"/><path d="M3 14h18"/><path d="M12 21v-4"/><path d="M10 19l2 2 2-2"/>';
    const ICON_COL_LEFT =
      '<path d="M10 3v18"/><path d="M14 3v18"/><path d="M18 3v18"/><path d="M3 12h4"/><path d="M5 10l-2 2 2 2"/>';
    const ICON_COL_RIGHT =
      '<path d="M6 3v18"/><path d="M10 3v18"/><path d="M14 3v18"/><path d="M21 12h-4"/><path d="M19 10l2 2-2 2"/>';
    const ICON_TRASH =
      '<path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>';

    // ---- Row operations ----
    const insertRowRelative = (row, table, position /* 'above' | 'below' */) => {
      const colCount = row.cells.length;
      const newRow = createEmptyRow(colCount, table);
      const parent = row.parentNode;
      if (position === 'above') {
        // Row belongs to thead or tbody — always insert in same parent to
        // preserve structure. The new row uses <td> cells even in thead,
        // which mirrors common HTML-table patterns without breaking rendering.
        parent.insertBefore(newRow, row);
      } else {
        const inThead = row.closest('thead') !== null;
        if (inThead) {
          const tbody = table.querySelector('tbody');
          if (tbody) tbody.insertBefore(newRow, tbody.firstChild);
          else parent.insertBefore(newRow, row.nextSibling);
        } else {
          parent.insertBefore(newRow, row.nextSibling);
        }
      }
    };

    const deleteRow = (row, table) => {
      const allRows = [...table.querySelectorAll('tr')];
      if (allRows.length <= 1) return;
      row.parentNode.removeChild(row);
    };

    // ---- Column operations ----
    const insertColumnRelative = (table, colIdx, position /* 'left' | 'right' */) => {
      table.querySelectorAll('tr').forEach(tr => {
        const ref = tr.cells[colIdx];
        const isHeaderRow = !!tr.querySelector('th') && tr.cells[colIdx]?.tagName === 'TH';
        const newCell = createEmptyCell(isHeaderRow, table);
        if (position === 'left') {
          if (ref) tr.insertBefore(newCell, ref);
          else tr.appendChild(newCell);
        } else {
          if (ref && tr.cells[colIdx + 1]) tr.insertBefore(newCell, tr.cells[colIdx + 1]);
          else tr.appendChild(newCell);
        }
      });
    };

    const deleteColumn = (table, colIdx) => {
      const colCount = table.rows[0]?.cells.length || 0;
      if (colCount <= 1) return;
      table.querySelectorAll('tr').forEach(tr => {
        if (tr.cells[colIdx]) tr.deleteCell(colIdx);
      });
    };

    // ---- Build toolbars ----
    const buildRowButtons = (row, table) => {
      const buttons = [];
      buttons.push(
        makeIconBtn('Insert row above', false, ICON_ROW_ABOVE, () => {
          insertRowRelative(row, table, 'above');
          hideToolbars();
          syncToContext();
        })
      );
      buttons.push(
        makeIconBtn('Insert row below', false, ICON_ROW_BELOW, () => {
          insertRowRelative(row, table, 'below');
          hideToolbars();
          syncToContext();
        })
      );
      const rowCount = table.querySelectorAll('tr').length;
      if (rowCount > 1) {
        buttons.push(
          makeIconBtn('Delete row', true, ICON_TRASH, () => {
            deleteRow(row, table);
            hideToolbars();
            syncToContext();
          })
        );
      }
      return buttons;
    };

    const buildColButtons = (colIdx, table) => {
      const buttons = [];
      buttons.push(
        makeIconBtn('Insert column left', false, ICON_COL_LEFT, () => {
          insertColumnRelative(table, colIdx, 'left');
          hideToolbars();
          syncToContext();
        })
      );
      buttons.push(
        makeIconBtn('Insert column right', false, ICON_COL_RIGHT, () => {
          insertColumnRelative(table, colIdx, 'right');
          hideToolbars();
          syncToContext();
        })
      );
      const colCount = table.rows[0]?.cells.length || 0;
      if (colCount > 1) {
        buttons.push(
          makeIconBtn('Delete column', true, ICON_TRASH, () => {
            deleteColumn(table, colIdx);
            hideToolbars();
            syncToContext();
          })
        );
      }
      return buttons;
    };

    // ---- Positioning & display ----
    const positionToolbars = () => {
      if (!activeCell || !activeTable) return;
      if (!document.body.contains(activeCell)) {
        hideToolbars();
        return;
      }
      const row = activeCell.closest('tr');
      if (!row) return;
      const cellRect = activeCell.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const tableRect = activeTable.getBoundingClientRect();

      if (rowToolbarEl && rowToolbarEl.style.display !== 'none') {
        const rect = rowToolbarEl.getBoundingClientRect();
        const tbW = rect.width || 36;
        const tbH = rect.height || 88;
        let top = rowRect.top + (rowRect.height || cellRect.height) / 2 - tbH / 2;
        let left = tableRect.left - tbW - 8;
        if (left < 4) left = 4;
        top = Math.max(4, Math.min(top, window.innerHeight - tbH - 4));
        rowToolbarEl.style.top = `${top}px`;
        rowToolbarEl.style.left = `${left}px`;
      }

      if (colToolbarEl && colToolbarEl.style.display !== 'none') {
        const rect = colToolbarEl.getBoundingClientRect();
        const tbW = rect.width || 88;
        const tbH = rect.height || 36;
        let top = tableRect.top - tbH - 8;
        let left = cellRect.left + cellRect.width / 2 - tbW / 2;
        if (top < 4) top = tableRect.top + 4; // clamp inside if no room above
        left = Math.max(4, Math.min(left, window.innerWidth - tbW - 4));
        colToolbarEl.style.top = `${top}px`;
        colToolbarEl.style.left = `${left}px`;
      }
    };

    const showToolbarsForCell = (cell, table) => {
      if (!cell || !table) return;

      const row = cell.closest('tr');
      if (!row) return;
      const allRows = [...table.querySelectorAll('tr')];
      const rowIdx = allRows.indexOf(row);
      const colIdx = Array.from(row.cells).indexOf(cell);
      if (rowIdx < 0 || colIdx < 0) return;

      const isFirstRowCell = rowIdx === 0;
      const isFirstColCell = colIdx === 0;

      // Non-qualifying cell: immediately hide any visible toolbar so the
      // handle doesn't linger while the user edits interior cells.
      if (!isFirstRowCell && !isFirstColCell) {
        hideToolbars();
        return;
      }

      // We actually have something to show — keep it visible.
      cancelHide();
      activeCell = cell;
      activeTable = table;

      // Row toolbar: only when the cell is in the first column.
      if (isFirstColCell) {
        const rowTb = getRowToolbar();
        rowTb.innerHTML = '';
        buildRowButtons(row, table).forEach(b => rowTb.appendChild(b));
        rowTb.style.display = 'flex';
      } else if (rowToolbarEl) {
        rowToolbarEl.style.display = 'none';
        rowToolbarEl.innerHTML = '';
      }

      // Column toolbar: only when the cell is in the first row.
      if (isFirstRowCell) {
        const colTb = getColToolbar();
        colTb.innerHTML = '';
        buildColButtons(colIdx, table).forEach(b => colTb.appendChild(b));
        colTb.style.display = 'flex';
      } else if (colToolbarEl) {
        colToolbarEl.style.display = 'none';
        colToolbarEl.innerHTML = '';
      }

      // Two rafs: first lets the browser layout the toolbars so we can measure.
      requestAnimationFrame(() => requestAnimationFrame(positionToolbars));
    };

    // ---- Dismiss on outside click / escape ----
    const isInsideToolbar = target =>
      Boolean(
        (rowToolbarEl && rowToolbarEl.contains(target)) ||
          (colToolbarEl && colToolbarEl.contains(target))
      );

    const onDocMouseDown = e => {
      if (!activeCell) return;
      const t = e.target;
      if (!t) return;
      if (isInsideToolbar(t)) return;
      if (typeof t.closest === 'function' && t.closest('.ql-table-embed')) return;
      hideToolbars();
    };

    const onDocKeyDown = e => {
      if (e.key === 'Escape' && activeCell) hideToolbars();
    };

    const onReflow = () => positionToolbars();

    // Capture-phase guards on the Quill editor root. Quill's clipboard
    // module also registers copy/cut/paste listeners on the root and will
    // otherwise run before (or alongside) our per-cell listeners, hijacking
    // the clipboard operation and inserting content outside the cell.
    const isInsideTableCell = target =>
      Boolean(
        target &&
          typeof target.closest === 'function' &&
          target.closest('.ql-table-embed') &&
          target.closest('th, td')
      );

    const onRootClipboardCapture = e => {
      if (!isInsideTableCell(e.target)) return;
      e.stopPropagation();
      if (e.type === 'paste' || e.type === 'cut') {
        setTimeout(syncToContext, 0);
      }
    };

    quill.root.addEventListener('copy', onRootClipboardCapture, true);
    quill.root.addEventListener('cut', onRootClipboardCapture, true);
    quill.root.addEventListener('paste', onRootClipboardCapture, true);

    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onDocKeyDown, true);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);

    // Re-apply cell handlers whenever Quill refreshes DOM.
    const refreshTableEmbeds = () => {
      quill.root.querySelectorAll('.ql-table-embed').forEach(embed => {
        const table = embed.querySelector('table');
        if (!table) return;
        makeAllCellsEditable(table);
      });
      // If the active cell was removed during a refresh, hide the toolbars.
      if (activeCell && !document.body.contains(activeCell)) hideToolbars();
    };

    const tid = setTimeout(refreshTableEmbeds, 200);
    quill.on('text-change', refreshTableEmbeds);

    return () => {
      clearTimeout(tid);
      cancelHide();
      quill.off('text-change', refreshTableEmbeds);
      quill.root.removeEventListener('copy', onRootClipboardCapture, true);
      quill.root.removeEventListener('cut', onRootClipboardCapture, true);
      quill.root.removeEventListener('paste', onRootClipboardCapture, true);
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onDocKeyDown, true);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
      if (rowToolbarEl) {
        rowToolbarEl.remove();
        rowToolbarEl = null;
      }
      if (colToolbarEl) {
        colToolbarEl.remove();
        colToolbarEl = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setSummaryContext]);
  // ================================================================
  // END TABLE INTERACTIVITY
  // ================================================================

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', height: '100%' }}>
      <Box
        sx={{
          width: '100%',
          //maxWidth: '960px',
          position: 'relative',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* Save Changes Button - Shows when there ARE changes */}
        {hasChanges && (
          <Button
            variant="contained"
            startIcon={<CheckCircleOutlineIcon />}
            sx={{
              position: 'absolute',
              top: isMobileView ? '40px' : '6px',
              right: '16px',
              zIndex: 10,
              bgcolor: '#E3F2FD',
              color: '#1E88E5',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#BBDEFB',
              },
            }}
            onClick={() => {
              props.handleSumbit(null, true, summaryContext);
              setInitialValue(quillValue);
              initialValueRef.current = quillValue;
              setHasChanges(false);
            }}
          >
            Save Changes
          </Button>
        )}

        {/* This Box is used to scope the CSS overrides for Quill */}
        <Box
          sx={{
            height: '100%',
            '.ql-toolbar.ql-snow': {
              border: 'none',
              borderBottom: '1px solid #e0e0e0',
              borderRadius: '8px 8px 0 0',
              padding: isMobileView ? '10px 8px' : '12px 16px',
              marginRight: '0px',
              // backgroundColor: '#f5f5f5',
              display: 'flex',
              flexWrap: 'wrap',
              gap: isMobileView ? '2px' : '4px',
              alignItems: 'center',
              ...(isMobileView
                ? { minHeight: '84px', maxHeight: '145px' }
                : { minHeight: '48px' }),
              // Group separator styling: only on mobile (desktop has no border-right)
              '& .ql-formats': {
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                marginRight: isMobileView ? '4px' : '8px',
                position: 'relative',
                '&:not(:last-child)::after': {
                  content: isMobileView ? '""' : 'none',
                  display: isMobileView ? 'block' : 'none',
                  position: 'absolute',
                  right: isMobileView ? '-6px' : '-10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '1px',
                  height: '24px',
                  backgroundColor: '#d0d0d0',
                },
              },
              // Button styling
              '& button': {
                width: '28px',
                height: '28px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                padding: '4px',
                margin: '0 1px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.06)',
                },
                '&.ql-active': {
                  backgroundColor: 'rgba(25, 118, 210, 0.12)',
                  color: '#1976d2',
                  '& svg': {
                    fill: '#1976d2',
                  },
                },
              },
              // Picker styling (Header, Font, etc.)
              '& .ql-picker': {
                color: '#424242',
                fontSize: '14px',
                '&.ql-header': {
                  '& .ql-picker-label': {
                    padding: '4px 8px',
                    minWidth: '80px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#1976d2',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    },
                    '&::before': {
                      fontWeight: 500,
                    },
                  },
                  '& .ql-picker-options': {
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    padding: '4px 0',
                    marginTop: '4px',
                  },
                  '& .ql-picker-item': {
                    padding: '6px 12px',
                    fontSize: '14px',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                    '&.ql-selected': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      fontWeight: 500,
                    },
                  },
                },
                '& .ql-picker-label': {
                  padding: '4px 8px',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                  },
                },
                '& .ql-picker-options': {
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  padding: '4px 0',
                },
                '& .ql-picker-item': {
                  padding: '6px 12px',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                  '&.ql-selected': {
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                  },
                },
              },
              // Icon colors and sizing
              '& .ql-bold, & .ql-italic, & .ql-underline, & .ql-blockquote, & .ql-list, & .ql-indent, & .ql-clean': {
                color: '#424242',
                '& svg': {
                  width: isMobileView ? '16px' : '18px',
                  height: isMobileView ? '16px' : '18px',
                },
              },
              // List button specific styling
              '& .ql-list': {
                '& button': {
                  '&.ql-list': {
                    marginRight: '2px',
                  },
                },
              },
              // Clean button styling
              '& .ql-clean': {
                '& button': {
                  '& svg': {
                    width: '16px',
                    height: '16px',
                  },
                },
              },
              // Undo/Redo styling
              '& .ql-undo, & .ql-redo': {
                width: '28px',
                height: '28px',
                color: '#9e9e9e',
                opacity: 0.6,
                '&.disabled': {
                  opacity: 0.35,
                  cursor: 'not-allowed',
                },
                '&:not(.disabled)': {
                  opacity: 1,
                  color: '#424242',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                  },
                },
              },
            },
            '.quill ': {
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            },
            '.ql-container.ql-snow': {
              border: 'none',
              flex: '1 1 auto',
              overflowY: ' auto',
              borderRadius: '0 0 8px 8px',
              backgroundColor: '#fff',
            },
            // Editor content area styling
            '.ql-editor': {
              padding: isMobileView ? '12px 16px' : '16px 24px',
              minHeight: '200px',
              fontSize: isMobileView ? '14px' : '16px',
              lineHeight: '1.6',
              '&.ql-blank::before': {
                fontStyle: 'normal',
                color: '#9e9e9e',
                fontSize: isMobileView ? '14px' : '16px',
              },
            },
            // Enhanced Markdown styling similar to Dillinger.io
            '.ql-editor': {
              '& .markdown-content': {
                fontFamily:
                  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: '16px',
                lineHeight: '1.6',
                color: '#333',
              },
              // Headings
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                marginTop: '24px',
                marginBottom: '16px',
                fontWeight: 600,
                lineHeight: '1.25',
                color: '#24292e',
              },
              '& h1': {
                fontSize: '2em',
                borderBottom: '1px solid #eaecef',
                paddingBottom: '0.3em',
                margin: isMobileView ? '4px 0px' : '16px',
              },
              '& h2': {
                fontSize: '1.5em',
                borderBottom: '1px solid #eaecef',
                paddingBottom: '0.3em',
              },
              '& h3': {
                fontSize: '1.25em',
              },
              '& h4': {
                fontSize: '1em',
              },
              '& h5': {
                fontSize: '0.875em',
              },
              '& h6': {
                fontSize: '0.85em',
                color: '#6a737d',
              },
              // Paragraphs
              '& p': {
                marginTop: '0',
                marginBottom: '16px',
              },
              // Lists - Disable CSS bullets completely, let Quill handle all list rendering
              '& ul, & ol': {
                marginTop: '0',
                marginBottom: '16px',
                paddingLeft: '1.5em',
                listStyleType: 'none', // Disable CSS bullets - Quill will handle them
                listStylePosition: 'outside',
              },
              '& li': {
                marginTop: '0.25em',
                marginBottom: '0.25em',
                display: 'list-item',
                paddingLeft: '0.3em',
                listStyleType: 'none', // Disable CSS bullets
                position: 'relative',
              },
              // Prevent double bullets - ensure nested lists have proper spacing
              '& li > ul, & li > ol': {
                marginTop: '0.5em',
                marginBottom: '0.5em',
                listStyleType: 'none', // Disable CSS bullets for nested lists too
              },
              // Ensure list items with paragraphs don't create extra bullets
              '& li > p': {
                marginTop: '0',
                marginBottom: '0',
                display: 'inline',
              },
              '& li + li': {
                marginTop: '0.25em',
              },
              // Quill-specific: Disable CSS bullets but allow Quill's ::before bullets to work
              '& .ql-editor ul, & .ql-editor ol': {
                listStyleType: 'none', // Disable CSS bullets - Quill uses ::before for bullets
                paddingLeft: '1.5em',
              },
              '& .ql-editor li': {
                listStyleType: 'none', // Disable CSS bullets on list items
                paddingLeft: '0.3em',
              },
              // Don't remove ::before - Quill uses it for bullets!
              // Only remove ::marker if it exists
              '& .ql-editor li::marker': {
                display: 'none',
              },
              // Also target any nested lists
              '& .ql-editor ul ul, & .ql-editor ol ol, & .ql-editor ul ol, & .ql-editor ol ul':
                {
                  listStyleType: 'none',
                },
              // Code blocks
              '& pre': {
                padding: '16px',
                overflow: 'auto',
                fontSize: '85%',
                lineHeight: '1.45',
                backgroundColor: '#f6f8fa',
                borderRadius: '6px',
                marginTop: '0',
                marginBottom: '16px',
                wordWrap: 'normal',
                '& code': {
                  display: 'inline',
                  padding: '0',
                  margin: '0',
                  overflow: 'visible',
                  lineHeight: 'inherit',
                  wordWrap: 'normal',
                  backgroundColor: 'transparent',
                  border: '0',
                  fontSize: '100%',
                },
              },
              '& code': {
                padding: '0.2em 0.4em',
                margin: '0',
                fontSize: '85%',
                backgroundColor: 'rgba(27, 31, 35, 0.05)',
                borderRadius: '3px',
                fontFamily:
                  'SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace',
              },
              '& pre code': {
                display: 'block',
                padding: '0',
                margin: '0',
                overflow: 'visible',
                lineHeight: 'inherit',
                wordWrap: 'normal',
                backgroundColor: 'transparent',
                border: '0',
              },
              // Table embed wrapper (Quill BlockEmbed for tables)
              '& .ql-table-embed': {
                overflowX: 'auto',
                margin: '16px 0',
                cursor: 'default',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f1f1f1',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: '#555',
                  },
                },
              },
              // Tables
              '& table, & .markdown-table': {
                borderSpacing: '0',
                borderCollapse: 'collapse',
                display: 'table',
                width: '100%',
                maxWidth: '100%',
                marginTop: '0',
                marginBottom: '0',
              },
              '& thead': {
                display: 'table-header-group',
              },
              '& tbody': {
                display: 'table-row-group',
              },
              '& tr': {
                backgroundColor: '#fff',
                borderTop: '1px solid #c6cbd1',
                '&:nth-of-type(2n)': {
                  backgroundColor: '#f6f8fa',
                },
              },
              '& th, & td': {
                padding: '6px 13px',
                border: '1px solid #dfe2e5',
                textAlign: 'left',
                // Focus ring for editable cells
                '&:focus': {
                  outline: '2px solid #1976d2',
                  outlineOffset: '-2px',
                  backgroundColor: '#f0f7ff',
                },
              },
              '& th': {
                fontWeight: 600,
                backgroundColor: '#f6f8fa',
              },
              '& td': {
                backgroundColor: 'inherit',
              },
              // Blockquotes
              '& blockquote': {
                padding: '0 1em',
                color: '#6a737d',
                borderLeft: '0.25em solid #dfe2e5',
                marginTop: '0',
                marginBottom: '16px',
                '& > :first-of-type': {
                  marginTop: '0',
                },
                '& > :last-child': {
                  marginBottom: '0',
                },
              },
              // Links
              '& a': {
                color: '#0366d6',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
              // Horizontal rules
              '& hr': {
                height: '0.25em',
                padding: '0',
                margin: '24px 0',
                backgroundColor: '#e1e4e8',
                border: '0',
              },
              // Images
              '& img': {
                maxWidth: '100%',
                boxSizing: 'content-box',
                backgroundColor: '#fff',
                marginTop: '0',
                marginBottom: '16px',
              },
              // Task lists
              '& .task-list-item': {
                listStyleType: 'none',
                '& input[type="checkbox"]': {
                  margin: '0 0.2em 0.25em -1.6em',
                  verticalAlign: 'middle',
                },
              },
            },
          }}
        >
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={quillValue}
            onChange={handleEditorChange}
            modules={quillModules}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default MeetingMinutesEditor;
